/**
 * ProductionScanDialog.jsx
 * Full-screen scan-to-count modal for a Production Order.
 * Lets the operator:
 *  - Scan raw-material component barcodes → POST /production-orders/:id/scan-consume
 *  - Scan finished-good barcodes → POST /production-orders/:id/scan-yield
 *
 * Shares the same UX primitives as Receiving scan mode: continuous/batch
 * submit toggle, carton multiplier, audio chirp, sonner toast feedback,
 * persistent scanner re-mount between scans.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import BarcodeScanner from './BarcodeScanner';
import { Camera, Lightning, Stack, Package, SpeakerHigh, SpeakerSlash, X, CheckCircle, ArrowClockwise, Factory } from '@phosphor-icons/react';
import { scanConsumeComponent, scanYieldUnit, getVariants, getSkus } from '../lib/api';
import { beep, isScanSoundEnabled, setScanSoundEnabled } from '../lib/scanBeep';
import { getCachedScanSettings } from '../lib/scanSettings';

export default function ProductionScanDialog({ po, onClose, onChanged }) {
  const [mode, setMode] = useState('consume'); // 'consume' | 'yield'
  const [submitMode, setSubmitMode] = useState(() => getCachedScanSettings().default_submit_mode);
  const [cartonMultiplier, setCartonMultiplier] = useState(() => getCachedScanSettings().default_carton_multiplier);
  const [soundOn, setSoundOn] = useState(() => isScanSoundEnabled());
  const [scannerOpen, setScannerOpen] = useState(false);

  // Data — variants + parent SKUs for pretty labels
  const [variants, setVariants] = useState([]);
  const [skus, setSkus] = useState([]);
  useEffect(() => {
    Promise.all([getVariants(), getSkus()]).then(([v, s]) => {
      setVariants(v.data);
      setSkus(s.data);
    }).catch(() => {});
  }, []);
  const variantsByBarcode = useMemo(() => {
    const m = {};
    for (const v of variants) if (v.barcode) m[String(v.barcode).toUpperCase()] = v;
    return m;
  }, [variants]);
  const skusById = useMemo(() => Object.fromEntries(skus.map(s => [s.id, s])), [skus]);
  const finishedSkuBarcodes = useMemo(() => {
    // For yield scans, we accept EITHER the finished-good SKU barcode OR any of its variants' barcodes
    const fgSku = skus.find(s => s.id === po.finished_good_sku_id);
    const out = new Set();
    if (fgSku?.barcode) out.add(String(fgSku.barcode).toUpperCase());
    for (const v of variants) if (v.sku_id === po.finished_good_sku_id && v.barcode) out.add(String(v.barcode).toUpperCase());
    return out;
  }, [skus, variants, po.finished_good_sku_id]);

  // Running tallies (server-side) and local batch counters
  const [consumed, setConsumed] = useState({}); // { variant_id: qty }
  const [yielded, setYielded] = useState(0);
  const [batchConsume, setBatchConsume] = useState({});
  const [batchYield, setBatchYield] = useState(0);
  // Hydrate initial tallies from the PO object
  useEffect(() => {
    const c = {};
    for (const e of (po.consumed_components || [])) c[e.variant_id] = (c[e.variant_id] || 0) + (e.quantity || 0);
    setConsumed(c);
    setYielded((po.yielded_units || []).reduce((a, e) => a + (e.quantity || 0), 0));
  }, [po.id, po.consumed_components, po.yielded_units]);

  // Lightweight feedback pill
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);
  const flash = (type, message) => {
    setFeedback({ type, message });
    beep(type === 'ok' ? 'ok' : type === 'warn' ? 'warn' : 'err');
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 900);
  };

  const cartonN = () => Math.max(1, parseInt(cartonMultiplier, 10) || 1);

  const handleScan = async (raw) => {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return;

    if (mode === 'consume') {
      const v = variantsByBarcode[code];
      if (!v) {
        flash('err', 'Unknown barcode');
        return;
      }
      // Guard: must be in BOM's components
      const isComponent = (po.components_availability || []).some((c) => c.variant_id === v.id);
      if (!isComponent) {
        flash('err', 'Not a component of this BOM');
        return;
      }
      const n = cartonN();
      if (submitMode === 'batch') {
        setBatchConsume((prev) => ({ ...prev, [v.id]: (prev[v.id] || 0) + n }));
        flash('ok', `+${n} · ${skusById[v.sku_id]?.sku_code || v.barcode}`);
      } else {
        try {
          const { data } = await scanConsumeComponent(po.id, { variant_id: v.id, quantity: n });
          setConsumed(data.consumed_by_variant || {});
          flash('ok', `✓ ${skusById[v.sku_id]?.sku_code || v.barcode}${n > 1 ? ` ×${n}` : ''}`);
        } catch (e) {
          flash('err', e?.response?.data?.detail || 'Scan rejected');
        }
      }
    } else {
      // Yield
      if (finishedSkuBarcodes.size > 0 && !finishedSkuBarcodes.has(code)) {
        flash('err', 'Not the finished-good barcode');
        return;
      }
      const n = cartonN();
      if (submitMode === 'batch') {
        setBatchYield((prev) => prev + n);
        flash('ok', `+${n} yielded`);
      } else {
        try {
          const { data } = await scanYieldUnit(po.id, { quantity: n });
          setYielded(data.total_yielded);
          flash('ok', `✓ yield ${data.total_yielded}/${data.target}`);
          if (data.total_yielded >= data.target) {
            toast.success('Target reached — advance the PO to finish.');
          }
        } catch (e) {
          flash('err', e?.response?.data?.detail || 'Scan rejected');
        }
      }
    }
  };

  const submitBatch = async () => {
    let ok = 0, failed = 0;
    if (mode === 'consume') {
      for (const [variantId, qty] of Object.entries(batchConsume)) {
        if (qty <= 0) continue;
        try {
          const { data } = await scanConsumeComponent(po.id, { variant_id: variantId, quantity: qty });
          setConsumed(data.consumed_by_variant || {});
          ok++;
        } catch { failed++; }
      }
      setBatchConsume({});
    } else if (batchYield > 0) {
      try {
        const { data } = await scanYieldUnit(po.id, { quantity: batchYield });
        setYielded(data.total_yielded);
        ok++;
      } catch { failed++; }
      setBatchYield(0);
    }
    if (failed > 0) toast.error(`Submitted ${ok}, ${failed} failed`);
    else if (ok > 0) toast.success('Batch submitted');
    if (onChanged) onChanged();
  };

  const consumedTotal = Object.values(consumed).reduce((a, b) => a + b, 0);
  const batchConsumeTotal = Object.values(batchConsume).reduce((a, b) => a + b, 0);

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="production-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory size={18} weight="fill" className="text-brand" />
            <span className="font-mono">{po.production_order_number}</span>
            <span className="text-muted-foreground text-sm">· {po.finished_good_name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Direction tabs */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-md" data-testid="scan-direction-tabs">
            <button
              type="button"
              onClick={() => { setMode('consume'); setScannerOpen(false); }}
              data-testid="mode-consume"
              className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition ${mode === 'consume' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Consume (raw)
            </button>
            <button
              type="button"
              onClick={() => { setMode('yield'); setScannerOpen(false); }}
              data-testid="mode-yield"
              className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition ${mode === 'yield' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Yield (finished)
            </button>
          </div>

          {/* Live tally */}
          <div className="bg-card border border-border rounded-md p-3 text-sm" data-testid="scan-tally">
            {mode === 'consume' ? (
              <>
                <p className="overline mb-2">Consumed components · Total {consumedTotal + batchConsumeTotal}</p>
                {Object.keys(consumed).length === 0 && batchConsumeTotal === 0 ? (
                  <p className="text-muted-foreground text-xs">No scans yet</p>
                ) : (
                  <div className="space-y-1">
                    {(po.components_availability || []).map((c) => {
                      const v = variants.find(x => x.id === c.variant_id);
                      const s = v && skusById[v.sku_id];
                      const serverQty = consumed[c.variant_id] || 0;
                      const batchQty = batchConsume[c.variant_id] || 0;
                      const total = serverQty + batchQty;
                      const needed = c.needed || 0;
                      const pct = needed > 0 ? Math.min(100, (total / needed) * 100) : 0;
                      return (
                        <div key={c.variant_id} className="text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono truncate">{s?.sku_code || v?.barcode || c.variant_id.slice(-8)}</span>
                            <span className="font-mono tabular-nums shrink-0">{total}/{needed}</span>
                          </div>
                          <div className="h-1 bg-muted rounded mt-1 overflow-hidden">
                            <div className={`h-full ${total >= needed ? 'bg-emerald-500' : 'bg-brand'}`} style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="overline mb-2">Yielded finished goods</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold tabular-nums" data-testid="yield-total">{yielded + batchYield}</span>
                  <span className="text-muted-foreground">/ {po.quantity} target</span>
                </div>
                <div className="h-1 bg-muted rounded mt-2 overflow-hidden">
                  <div
                    className={`h-full ${(yielded + batchYield) >= po.quantity ? 'bg-emerald-500' : 'bg-brand'}`}
                    style={{ width: `${Math.min(100, ((yielded + batchYield) / Math.max(1, po.quantity)) * 100)}%` }}
                  ></div>
                </div>
              </>
            )}
          </div>

          {/* Submit mode + carton + sound — same pattern as Receiving */}
          <div>
            <Label className="overline">Scan submit mode</Label>
            <div className="grid grid-cols-2 gap-1 mt-1 p-1 bg-muted rounded-md">
              <button
                type="button"
                onClick={() => setSubmitMode('continuous')}
                data-testid="submit-mode-continuous"
                className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1 ${submitMode === 'continuous' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Lightning size={12} weight="fill" /> Continuous
              </button>
              <button
                type="button"
                onClick={() => setSubmitMode('batch')}
                data-testid="submit-mode-batch"
                className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1 ${submitMode === 'batch' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Stack size={12} weight="fill" /> Batch
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline flex items-center gap-1"><Package size={10} weight="fill" /> Carton qty</Label>
              <Input
                type="number" min={1} max={9999} inputMode="numeric"
                value={cartonMultiplier}
                onChange={(e) => setCartonMultiplier(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="mt-1 font-mono"
                data-testid="carton-multiplier-input"
              />
            </div>
            <div>
              <Label className="overline">Scan sound</Label>
              <button
                type="button"
                onClick={() => { const next = !soundOn; setSoundOn(next); setScanSoundEnabled(next); if (next) beep('ok'); }}
                data-testid="scan-sound-toggle"
                className={`mt-1 w-full h-10 rounded-md border flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition ${soundOn ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-muted border-border text-muted-foreground'}`}
              >
                {soundOn ? <SpeakerHigh size={14} weight="fill" /> : <SpeakerSlash size={14} weight="fill" />}
                {soundOn ? 'On' : 'Muted'}
              </button>
            </div>
          </div>

          <Button
            onClick={() => setScannerOpen(true)}
            data-testid="open-scanner-btn"
            className="w-full h-12 bg-brand hover:bg-brand/90 font-bold"
          >
            <Camera size={18} className="mr-2" weight="fill" /> Open scanner
          </Button>

          {submitMode === 'batch' && (batchConsumeTotal > 0 || batchYield > 0) && (
            <Button
              onClick={submitBatch}
              data-testid="submit-batch-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold"
            >
              <CheckCircle size={16} className="mr-2" /> Submit batch ({mode === 'consume' ? batchConsumeTotal : batchYield} units)
            </Button>
          )}
        </div>

        {/* Scanner overlay — stays open between scans (via re-mount) */}
        {scannerOpen && (
          <BarcodeScanner
            title={`${mode === 'consume' ? 'Consume' : 'Yield'} · ${po.production_order_number}`}
            onScan={async (code) => {
              setScannerOpen(false);
              await handleScan(code);
              setTimeout(() => setScannerOpen(true), 300);
            }}
            onClose={() => setScannerOpen(false)}
          />
        )}

        {/* Feedback pill */}
        {feedback && (
          <div
            className={`fixed top-20 left-1/2 -translate-x-1/2 z-[80] px-4 py-2 rounded-full shadow-lg text-sm font-bold uppercase tracking-wider ${
              feedback.type === 'ok' ? 'bg-emerald-600 text-white' : feedback.type === 'warn' ? 'bg-amber-500 text-white' : 'bg-rose-600 text-white'
            }`}
            data-testid="scan-feedback"
          >
            {feedback.message}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
